/**
 * iter129 — QuoteDetail page
 * Route: /quotes/:quoteId
 *
 * Shows all line items of the quote, internal notes, SMS history, and
 * an editable SMS body for follow-up reminders. SM/FM/SA only.
 */
import { useEffect, useState, useCallback } from 'react';
import { useCommsGate } from '@/components/CommsUpgradeDialog';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import QuoteBuilder, { buildQuoteSmsBody } from '@/components/QuoteBuilder';
import { formatAUMobile, formatRego } from '@/lib/formatters';
import { toast } from 'sonner';
import { ArrowLeft, PhoneIncoming, Send, Trash2, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_TONE = {
  draft: 'bg-slate-100 text-slate-700',
  sent: 'bg-blue-100 text-blue-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  declined: 'bg-rose-100 text-rose-700',
  expired: 'bg-amber-100 text-amber-700',
};

export default function QuoteDetail() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [savingRows, setSavingRows] = useState(false);
  // SMS dialog state
  const [smsOpen, setSmsOpen] = useState(false);
  const { guardComms, commsDialog } = useCommsGate();
  const [smsBody, setSmsBody] = useState('');
  const [sendingSms, setSendingSms] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/quotes/${quoteId}`, { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        setQuote(data);
        setRows(data.line_items || []);
      } else if (r.status === 404) {
        toast.error('Quote not found');
        navigate('/customers');
      }
    } catch (_e) {
      toast.error('Network error');
    } finally { setLoading(false); }
  }, [quoteId, navigate]);

  useEffect(() => { load(); }, [load]);

  const saveRows = async () => {
    setSavingRows(true);
    try {
      const cleaned = rows.filter(r => (r.description || '').trim()).map(r => ({
        ...r,
        quantity: Number(r.quantity || 1),
        parts_cost_price: r.parts_cost_price === '' ? null : Number(r.parts_cost_price),
        parts_price: r.parts_price === '' ? null : Number(r.parts_price),
        parts_margin_pct: r.parts_margin_pct === '' ? null : Number(r.parts_margin_pct),
        labour_hours: r.labour_hours === '' ? null : Number(r.labour_hours),
        labour_rate: r.labour_rate === '' ? null : Number(r.labour_rate),
        line_total: Number(r.line_total || 0),
      }));
      const r = await fetch(`${BACKEND_URL}/api/quotes/${quoteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ line_items: cleaned }),
      });
      if (r.ok) {
        const data = await r.json();
        setQuote(data);
        setRows(data.line_items || []);
        toast.success('Quote saved');
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save');
      }
    } catch (_e) {
      toast.error('Network error');
    } finally { setSavingRows(false); }
  };

  const updateField = async (patch) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/quotes/${quoteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(patch),
      });
      if (r.ok) {
        const data = await r.json();
        setQuote(data);
        toast.success('Updated');
      } else {
        toast.error('Failed to update');
      }
    } catch (_e) { /* noop */ }
  };

  const openFollowUpDialog = () => {
    if (!quote) return;
    const isFollowUp = (quote.sms_sent_count || 0) > 0;
    const baseRows = rows;
    const body = isFollowUp
      ? `Hi ${quote.customer_name},\n\nFollowing up on the quote we sent you${quote.vehicle_rego ? ` for ${quote.vehicle_rego}` : ''} (${quote.quote_number}).\n\nTotal: $${Number(quote.total || 0).toFixed(2)} incl GST.\n\nLet us know if you'd like to book in or have questions.\n\n— Hoppers Service Centre`
      : buildQuoteSmsBody({
          rows: baseRows,
          customerName: quote.customer_name,
          vehicleRego: quote.vehicle_rego,
          quoteNumber: quote.quote_number,
        });
    setSmsBody(body);
    setSmsOpen(true);
  };

  const confirmSendSms = async () => {
    setSendingSms(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/quotes/${quoteId}/send-sms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ body: smsBody, is_followup: (quote?.sms_sent_count || 0) > 0 }),
      });
      if (r.ok) {
        const data = await r.json();
        toast.success(`SMS delivered to ${data.sent_count} number(s)`);
        setSmsOpen(false);
        load();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(typeof err.detail === 'string' ? err.detail : 'SMS failed');
      }
    } catch (_e) {
      toast.error('Network error');
    } finally { setSendingSms(false); }
  };

  if (loading) return (
    <Layout>
      <div className="p-8 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
      </div>
    </Layout>
  );
  if (!quote) return null;

  const tone = STATUS_TONE[quote.status] || STATUS_TONE.draft;

  return (
    <Layout>
      <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-4" data-testid="quote-detail-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate(-1)} data-testid="quote-detail-back">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <span className="font-mono text-indigo-700">{quote.quote_number}</span>
                <Badge className={`${tone} text-xs`}>{quote.status}</Badge>
              </h1>
              <p className="text-xs text-slate-500">
                Created {new Date(quote.created_at).toLocaleString()}
                {quote.sent_at && <> · sent {new Date(quote.sent_at).toLocaleString()}</>}
                {quote.sms_sent_count > 0 && <> · <span className="text-indigo-700">{quote.sms_sent_count} SMS sent</span></>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={guardComms(openFollowUpDialog)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="quote-followup-sms-btn">
              <Send className="h-4 w-4 mr-1" />
              {(quote.sms_sent_count || 0) > 0 ? 'Send follow-up SMS' : 'Send Quote SMS'}
            </Button>
            <Select value={quote.status} onValueChange={(v) => updateField({ status: v })}>
              <SelectTrigger className="h-9 text-xs w-32" data-testid="quote-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">draft</SelectItem>
                <SelectItem value="sent">sent</SelectItem>
                <SelectItem value="accepted">accepted</SelectItem>
                <SelectItem value="declined">declined</SelectItem>
                <SelectItem value="expired">expired</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Customer + vehicle */}
        <Card className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Customer</Label>
            <p className="text-lg font-semibold text-slate-900">{quote.customer_name}</p>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div>
                <Label className="text-[10px] uppercase tracking-wide text-slate-500">Mobile</Label>
                <Input
                  defaultValue={quote.customer_phone || ''}
                  onBlur={(e) => updateField({ customer_phone: formatAUMobile(e.target.value) })}
                  className="h-8 text-sm"
                  data-testid="quote-customer-mobile"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wide text-slate-500">Landline</Label>
                <Input
                  defaultValue={quote.customer_landline || ''}
                  onBlur={(e) => updateField({ customer_landline: formatAUMobile(e.target.value) })}
                  className="h-8 text-sm"
                  data-testid="quote-customer-landline"
                />
              </div>
            </div>
            <Label className="text-[10px] uppercase tracking-wide text-slate-500 mt-2 block">Email</Label>
            <Input
              defaultValue={quote.customer_email || ''}
              onBlur={(e) => updateField({ customer_email: e.target.value })}
              className="h-8 text-sm"
              data-testid="quote-customer-email"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Vehicle</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              <Input
                defaultValue={quote.vehicle_rego || ''}
                onBlur={(e) => updateField({ vehicle_rego: formatRego(e.target.value) })}
                placeholder="Rego"
                className="h-8 text-sm font-mono"
                data-testid="quote-vehicle-rego"
              />
              <Input
                defaultValue={quote.vehicle_make || ''}
                onBlur={(e) => updateField({ vehicle_make: e.target.value })}
                placeholder="Make"
                className="h-8 text-sm"
                data-testid="quote-vehicle-make"
              />
              <Input
                defaultValue={quote.vehicle_model || ''}
                onBlur={(e) => updateField({ vehicle_model: e.target.value })}
                placeholder="Model"
                className="h-8 text-sm"
                data-testid="quote-vehicle-model"
              />
            </div>
            <Label className="text-xs uppercase tracking-wide text-slate-500 mt-3 block">Internal notes (SM only — what the customer said)</Label>
            <Textarea
              defaultValue={quote.internal_notes || ''}
              onBlur={(e) => updateField({ internal_notes: e.target.value })}
              rows={3}
              className="text-sm"
              data-testid="quote-internal-notes"
            />
          </div>
        </Card>

        {/* Line items */}
        <Card className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-base font-semibold text-slate-900">Line items</h2>
            <Button size="sm" onClick={saveRows} disabled={savingRows} className="h-8" data-testid="quote-save-lines">
              <Save className="h-3 w-3 mr-1" />
              {savingRows ? 'Saving…' : 'Save line items'}
            </Button>
          </div>
          <QuoteBuilder rows={rows} onChange={setRows} showOrp={true} />
        </Card>

        {/* SMS history */}
        {quote.last_sms_body && (
          <Card className="p-4 bg-slate-50">
            <h2 className="text-base font-semibold text-slate-900 mb-2">Last SMS sent</h2>
            <p className="text-[11px] text-slate-500 mb-1">
              Sent {quote.last_sms_sent_at ? new Date(quote.last_sms_sent_at).toLocaleString() : '—'}
              · {quote.sms_sent_count} SMS total
            </p>
            <pre className="text-xs whitespace-pre-wrap font-mono bg-white p-3 rounded border border-slate-200">
              {quote.last_sms_body}
            </pre>
          </Card>
        )}
      </div>

      {commsDialog}
      {/* Send SMS dialog */}
      <Dialog open={smsOpen} onOpenChange={(v) => { if (!v) setSmsOpen(false); }}>
        <DialogContent className="sm:max-w-lg" data-testid="quote-sms-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PhoneIncoming className="h-5 w-5 text-indigo-600" />
              {(quote.sms_sent_count || 0) > 0 ? 'Send follow-up SMS' : 'Send Quote SMS'}
            </DialogTitle>
            <DialogDescription>
              Edit the message below, then confirm to send via SMS to{' '}
              <span className="font-mono">{quote.customer_phone || quote.customer_landline || '—'}</span>.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={smsBody}
            onChange={(e) => setSmsBody(e.target.value)}
            rows={12}
            className="font-mono text-xs"
            data-testid="quote-sms-body"
          />
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setSmsOpen(false)} disabled={sendingSms} data-testid="quote-sms-cancel">
              Cancel
            </Button>
            <Button
              onClick={confirmSendSms}
              disabled={sendingSms || !smsBody.trim()}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="quote-sms-confirm"
            >
              <Send className="h-4 w-4 mr-1" />
              {sendingSms ? 'Sending…' : 'Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
